import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import merge from "lodash/merge";
import { Alert } from "components/bootstrap";
import Spinner from "components/common/Spinner";
import AggregationWidgetConfig from "views/logic/aggregationbuilder/AggregationWidgetConfig";
import Pivot, { DateType } from "views/logic/aggregationbuilder/Pivot";
import Series from "views/logic/aggregationbuilder/Series";
import { fetchEventsHistogram, parseTypeFilter } from "components/events/fetchEvents";
import FullSizeContainer from "views/components/aggregationbuilder/FullSizeContainer";
import InteractiveContext from "views/components/contexts/InteractiveContext";
import PlotLegend from "views/components/visualizations/PlotLegend";
import GenericPlot from "views/components/visualizations/GenericPlot";
import useUserDateTime from "hooks/useUserDateTime";
import { toUTCFromTz } from "util/DateTime";
import useOnRefresh from "components/common/PaginatedEntityTable/useOnRefresh";
const config = AggregationWidgetConfig.builder().visualization("area").rowPivots([Pivot.create(["timestamp"], DateType)]).columnPivots([Pivot.createValues(["type"])]).series([Series.forFunction("count()")]).rollup(false).build();
const DEFAULT_HEIGHT = "180px";
const GraphContainer = styled.div`
  height: ${({ $height }) => $height};
  width: 100%;
`;
const generateChart = (type, buckets, formatTime) => {
  const x = buckets.map((b) => formatTime(b.start_date, "internal"));
  const y = buckets.map((b) => b.count);
  return {
    type: "scatter",
    name: type,
    x,
    y,
    fill: "tozeroy",
    originalName: type,
    line: {
      shape: "linear",
      color: type === "Alerts" ? "#4478b3" : "#fd9e48"
    },
    yaxis: "y",
    fullPath: `${type}\u2E31count()`
  };
};
const yLegendPosition = (containerHeight) => {
  if (containerHeight < 150) {
    return -0.6;
  }
  if (containerHeight < 400) {
    return -0.2;
  }
  return -0.14;
};
const defaultSetColor = (chart, colors) => {
  var _a;
  return {
    line: { color: colors.get((_a = chart.originalName) != null ? _a : chart.name) }
  };
};
const layout = {
  yaxis: {
    fixedrange: true,
    rangemode: "tozero",
    tickformat: ",~r",
    type: "linear"
  },
  dragmode: "zoom",
  hovermode: "x",
  xaxis: {
    domain: [0, 1],
    type: "date"
  }
};
const prepareTimeRangeForGraph = (timerange, formatTime) => [
  formatTime(timerange.from, "internal"),
  formatTime(timerange.to, "internal")
];
const EventsGraph = ({
  data: { results, timerange },
  alerts,
  onZoom,
  formatTime,
  height,
  readOnly
}) => {
  const chartData = useMemo(
    () => [
      ...["include", "exclude"].includes(alerts) ? [generateChart("Events", results.buckets.events, formatTime)] : [],
      ...["include", "only"].includes(alerts) ? [generateChart("Alerts", results.buckets.alerts, formatTime)] : []
    ],
    [alerts, results.buckets.alerts, results.buckets.events, formatTime]
  );
  const baseLayout = useMemo(
    () => ({
      ...layout,
      xaxis: {
        ...layout.xaxis,
        range: prepareTimeRangeForGraph(timerange, formatTime)
      }
    }),
    [timerange, formatTime]
  );
  return /* @__PURE__ */ React.createElement(GraphContainer, { $height: height }, /* @__PURE__ */ React.createElement(InteractiveContext.Provider, { value: "disabled" }, /* @__PURE__ */ React.createElement(FullSizeContainer, null, (dimensions) => /* @__PURE__ */ React.createElement(PlotLegend, { config, chartData, height: dimensions.height, width: dimensions.width }, /* @__PURE__ */ React.createElement(InteractiveContext.Provider, { value: readOnly ? "read-only" : "interactive" }, /* @__PURE__ */ React.createElement(
    GenericPlot,
    {
      chartData,
      layout: merge({}, baseLayout, { legend: { y: yLegendPosition(dimensions.height) } }),
      onZoom,
      setChartColor: defaultSetColor
    }
  ))))));
};
const EventsHistogram = ({
  searchParams,
  setFilters,
  eventsHistogramFetcher = fetchEventsHistogram,
  height = DEFAULT_HEIGHT,
  readOnly = false,
  onEffectiveTimeRangeChange = void 0
}) => {
  var _a, _b, _c;
  const { userTimezone, formatTime } = useUserDateTime();
  const { data, isLoading, refetch, isError, error } = useQuery({
    queryKey: ["events", "histogram", searchParams],
    queryFn: () => eventsHistogramFetcher(searchParams),
    placeholderData: keepPreviousData
  });
  useOnRefresh(refetch);
  useEffect(() => {
    if (data) {
      onEffectiveTimeRangeChange == null ? void 0 : onEffectiveTimeRangeChange(data.timerange);
    }
  }, [data, onEffectiveTimeRangeChange]);
  const alerts = parseTypeFilter((_b = (_a = searchParams == null ? void 0 : searchParams.filters) == null ? void 0 : _a.get("alert")) == null ? void 0 : _b[0]);
  const onZoom = useCallback(
    (from, to) => {
      const parsedFrom = formatTime(toUTCFromTz(from, userTimezone), "internal");
      const parsedTo = formatTime(toUTCFromTz(to, userTimezone), "internal");
      setFilters(searchParams.filters.set("timestamp", [`${parsedFrom}><${parsedTo}`]));
    },
    [formatTime, searchParams.filters, setFilters, userTimezone]
  );
  if (isLoading) {
    return /* @__PURE__ */ React.createElement(Spinner, null);
  }
  if (isError || !data) {
    return /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger" }, "Loading events histogram failed: ", (_c = error == null ? void 0 : error.message) != null ? _c : "Unknown error");
  }
  return /* @__PURE__ */ React.createElement(
    EventsGraph,
    {
      data,
      alerts,
      onZoom,
      formatTime,
      height,
      readOnly
    }
  );
};
export default EventsHistogram;
